/**
 * デッキの画面（一覧・デッキのページ・編集ページ）で共有する部品（Issue #954）:
 * 禁止・制限の印（public/lflist.conf）・デッキイラスト / スリーブを選ぶモーダル・カード 1 枚の絵（型番の絵が無い印つき）。
 */
import { useState, useEffect } from 'react';
import Pressable from '../Pressable';
import { api } from '../../api/api';
import { DECK_IMAGE_SIZES, fitImageFile } from '../../utils/imageFit';

// lflist.conf のパース・キャッシュ
let _lflistCache = null;
async function loadLflist() {
  if (_lflistCache) return _lflistCache;
  const res = await fetch('/lflist.conf');
  const text = await res.text();
  const map = new Map();
  for (const line of text.split('\n')) {
    const m = line.match(/^(\d+)\s+([012])\s/);
    if (m) map.set(String(parseInt(m[1], 10)), Number(m[2]));
  }
  _lflistCache = map;
  return map;
}
export function useLflist() {
  const [lflist, setLflist] = useState(null);
  useEffect(() => { loadLflist().then(setLflist); }, []);
  return lflist;
}

export function LimitBadge({ cardId, lflist }) {
  if (!lflist || !cardId) return null;
  const limit = lflist.get(String(parseInt(cardId, 10)));
  if (limit === undefined) return null;

  const size = 22;
  const commonStyle = {
    position: 'absolute', top: 1, left: 1,
    width: size, height: size,
    borderRadius: '50%',
    background: '#000',
    border: '3px solid #cc0000',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 10,
    pointerEvents: 'none',
    flexShrink: 0,
    boxSizing: 'border-box',
  };

  if (limit === 0) {
    // 禁止: 斜め線
    return (
      <div style={commonStyle}>
        <div style={{
          position: 'absolute',
          width: '80%', height: '3px',
          background: '#cc0000',
          transform: 'rotate(-45deg)',
          borderRadius: 1,
        }} />
      </div>
    );
  }

  // 制限(1) / 準制限(2): 黄色い数字
  return (
    <div style={commonStyle}>
      <span style={{
        color: '#ffe000',
        fontSize: size * 0.62,
        fontWeight: 'bold',
        fontFamily: 'Arial Black, Arial, sans-serif',
        lineHeight: 1,
        textShadow: '0 0 2px #000',
        userSelect: 'none',
        transform: 'rotate(8deg)',
      }}>
        {limit}
      </span>
    </div>
  );
}

/** カード 1 枚の絵。型番の画像が無い（missing）・型番が決まっていない（unspecified）ときは右下に小さな印 */
export function CardThumb({ card, lflist, dim = false, children }) {
  const mark = card.image_status === 'missing' ? { text: '絵なし', color: '#e94560' }
    : card.image_status === 'unspecified' ? { text: '型番なし', color: '#888' } : null;
  return (
    <div style={{ position: 'relative' }} title={card.card_name}>
      {card.thumbnail_url
        ? <img src={card.thumbnail_url} alt={card.card_name} style={{ width: '100%', display: 'block', borderRadius: 2, opacity: dim ? 0.3 : 1 }} />
        : <div style={{ aspectRatio: '59/80', background: '#2a3a5c', borderRadius: 2, border: '1px solid #3a5080',
          fontSize: '0.55rem', color: '#aaa', padding: 2, overflow: 'hidden' }}>{card.card_name}</div>}
      <LimitBadge cardId={card.card_id} lflist={lflist} />
      {mark && (
        <span style={{ position: 'absolute', right: 1, bottom: 1, fontSize: '0.5rem', lineHeight: 1.3, padding: '0 2px', borderRadius: 2,
          background: 'rgba(0,0,0,0.75)', color: mark.color, pointerEvents: 'none' }}>{mark.text}</span>
      )}
      {children}
    </div>
  );
}

export function DeckImageSelectModal({ deck, field, label, listAction, uploadAction, s3Prefix, onClose, onSaved, onSelect }) {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const currentKey = deck[field] || '';

  useEffect(() => {
    // 一覧は軽いサムネイルで（GetDeckImages の thumbnail_url。listAction は前の呼び手のために受けるだけ）
    api.project('GetDeckImages', { kind: field === 'deck_illust' ? 'illust' : 'sleeve' })
      .then((rows) => setImages((rows ?? []).map((r) => ({ ...r, thumb: r.thumbnail_url || r.url }))))
      .catch(() => api.project(listAction).then(setImages));
  }, [field, listAction]);

  const handleSelect = async (s3Key, url) => {
    if (onSelect) {
      onSelect(s3Key, url);
      return;
    }
    try {
      await api.project('SetParam', {
        table_name: 'decks',
        record_id: { id: deck.id },
        param: { [field]: s3Key },
      });
      onSaved();
      onClose();
    } catch (err) {
      alert('更新に失敗しました: ' + err.message);
    }
  };

  const handleUpload = async (e) => {
    const original = e.target.files[0];
    if (!original) return;
    setUploading(true);
    try {
      const file = await fitImageFile(original, ...DECK_IMAGE_SIZES[field === 'deck_illust' ? 'illust' : 'sleeve']);   // 今の画像と同じ比率・大きさに
      const { upload_link, filename } = await api.project(uploadAction, { filename: file.name });
      await fetch(upload_link, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      const newKey = `${s3Prefix}${filename}`;
      const uploaded = images.find(i => i.key === newKey);
      await handleSelect(newKey, uploaded?.url || '');
    } catch (err) {
      alert('アップロードに失敗しました: ' + err.message);
      setUploading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 600, width: '95vw' }} onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">{label}を選択 — {deck.name}</h2>
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <label className={`btn btn-secondary btn-sm${uploading ? ' disabled' : ''}`} style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'アップロード中...' : '画像をアップロード'}
            <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUpload} disabled={uploading} />
          </label>
          {currentKey && (
            <button className="btn btn-secondary btn-sm" onClick={() => handleSelect('', '')}>クリア</button>
          )}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, maxHeight: '55vh', overflowY: 'auto' }}>
          {images.map(img => (
            <Pressable
              as="img"
              key={img.key}
              src={img.thumb || img.url}
              alt={img.key}
              title={img.key.replace(s3Prefix, '')}
              style={{
                width: 80, height: 80, objectFit: 'cover', cursor: 'pointer', borderRadius: 4,
                border: img.key === currentKey ? '2px solid #e94560' : '2px solid transparent',
              }}
              onClick={() => handleSelect(img.key, img.url)}
              hoverStyle={img.key === currentKey ? null : { border: '2px solid #666' }}
            />
          ))}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>キャンセル</button>
        </div>
      </div>
    </div>
  );
}

